"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

import type { NavLink, NavSettings } from "@/lib/nav";
import { regionFromPathname, type Region } from "@/lib/region";
import { resolveLink } from "@/lib/storyblok-link";

type LinkRow = { key: string; label: string; href: string; external: boolean };

function toLinkRows(items: NavLink[] | undefined, region: Region): LinkRow[] {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => {
      const label = (item.title || "").trim();
      const { href, target } = resolveLink(item.link, region);
      if (!label || href === "#") return null;
      return { key: item._uid, label, href, external: target === "_blank" };
    })
    .filter((row): row is LinkRow => row !== null);
}

const POLICY_CLASS =
  "text-[12px] leading-5 text-dusk-03 transition-colors hover:text-white";

const HummWordmark = (
  <span
    role="img"
    aria-label="humm"
    className="inline-block h-3 w-[57px] bg-current"
    style={{
      maskImage: "url(/assets/logo/humm-logo-w.svg)",
      WebkitMaskImage: "url(/assets/logo/humm-logo-w.svg)",
      maskRepeat: "no-repeat",
      WebkitMaskRepeat: "no-repeat",
      maskSize: "contain",
      WebkitMaskSize: "contain",
      maskPosition: "center",
      WebkitMaskPosition: "center",
    }}
  />
);

export default function LpFooter({
  navSettings,
}: {
  navSettings: NavSettings | null;
}) {
  const pathname = usePathname();
  const region = regionFromPathname(pathname || "/");
  const year = new Date().getFullYear();
  const policyRows = toLinkRows(navSettings?.policy_links, region);

  return (
    <footer className="bg-midnight text-white">
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-start justify-between gap-3 px-[clamp(1.25rem,4vw,2.5rem)] py-5 md:flex-row md:items-center md:gap-6">
        <div className="flex flex-col gap-1 text-[12px] leading-5 text-dusk-03 md:flex-row md:flex-wrap md:items-center md:gap-x-3 md:gap-y-1">
          <span>©Thérapie Fertility {year}</span>
          {policyRows.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              {policyRows.map((row, i) => (
                <span key={row.key} className="flex items-center gap-x-2">
                  {i > 0 && <span aria-hidden className="text-dusk-04">|</span>}
                  {row.external ? (
                    <a
                      href={row.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={POLICY_CLASS}
                    >
                      {row.label}
                    </a>
                  ) : (
                    <Link href={row.href} className={POLICY_CLASS}>
                      {row.label}
                    </Link>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="inline-flex items-center gap-2 text-[12px] leading-5 text-dusk-03">
          <span>Flexible Payments Plans with</span>
          {HummWordmark}
        </div>
      </div>
    </footer>
  );
}
